import { useState } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db9kqb46oi3g3tv8v0b0";

type Status = "idle" | "loading" | "success" | "error";

export default function ContactPage() {
  usePageMeta({
    title: "联系反馈 - 黑料吃瓜",
    description:
      "通过黑料吃瓜联系反馈页面提交爆料线索、内容纠错或合作咨询。请尽量提供时间、地点与可核实的细节，方便编辑跟进。",
    keywords: "联系黑料吃瓜,爆料线索,内容纠错,合作咨询,反馈表单",
  });

  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honey = String(formData.get("website_alt") ?? "").trim();
    if (honey) {
      setStatus("success");
      form.reset();
      setMessageLength(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("loading");
    setErrorMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setErrorMessage(serverMessage || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      form.reset();
      setMessageLength(0);
    } catch {
      setStatus("error");
      setErrorMessage("网络异常，提交未完成，请稍后重试。");
    }
  };

  return (
    <div className="max-w-[1320px] mx-auto px-4 md:px-6 py-5 md:py-7">
      <Breadcrumb items={[{ label: "联系反馈" }]} />

      <header className="mt-3 mb-5 md:mb-6">
        <h1 className="font-heading text-2xl md:text-[30px] font-black text-foreground-950">联系与爆料</h1>
        <p className="text-[13px] md:text-sm text-foreground-500 leading-7 mt-2 max-w-3xl">
          有线索、有疑问，或者发现内容有误？填写下面的表单告诉我们。编辑会在收到后的 48 小时内完成首轮核实与回复。
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
        <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
          {status === "success" ? (
            <div className="py-10 text-center">
              <span className="w-14 h-14 rounded-full bg-accent-50 flex items-center justify-center mx-auto">
                <i className="ri-check-line text-2xl text-accent-600" />
              </span>
              <h2 className="font-heading text-xl font-black text-foreground-900 mt-4">已收到，谢谢你</h2>
              <p className="text-[13.5px] text-foreground-500 leading-7 mt-2 max-w-md mx-auto">
                编辑会尽快核对线索中的时间、地点与关键细节。如果内容需要进一步确认，我们会通过你留下的邮箱联系。
              </p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="inline-flex items-center gap-1.5 mt-5 px-4 h-9 rounded-md bg-primary-500 text-background-50 text-[13px] whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                <i className="ri-add-line" />
                再提交一条
              </button>
            </div>
          ) : (
            <form
              id="contact-tip-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4"
              noValidate={false}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-[12.5px] text-foreground-600 mb-1.5">
                    姓名 / 昵称<span className="text-foreground-400 ml-1">（选填）</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    placeholder="怎么称呼你"
                    className="w-full h-10 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-[12.5px] text-foreground-600 mb-1.5">
                    电子邮箱<span className="text-accent-600 ml-1">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="w-full h-10 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-type" className="block text-[12.5px] text-foreground-600 mb-1.5">
                  反馈类型
                </label>
                <select
                  id="contact-type"
                  name="type"
                  defaultValue="爆料线索"
                  className="w-full h-10 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-400 transition-colors cursor-pointer"
                >
                  <option value="爆料线索">爆料线索</option>
                  <option value="内容纠错">内容纠错</option>
                  <option value="合作咨询">合作咨询</option>
                  <option value="侵权投诉">侵权投诉</option>
                  <option value="其他反馈">其他反馈</option>
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="contact-message" className="text-[12.5px] text-foreground-600">
                    留言内容<span className="text-accent-600 ml-1">*</span>
                  </label>
                  <span className="text-[11.5px] text-foreground-400">{messageLength} / 500</span>
                </div>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={7}
                  onChange={(event) => setMessageLength(event.target.value.length)}
                  placeholder="请尽量写清时间、地点、人物与可核实的细节，例如：某活动日期、现场情况、可提供的证明材料。请勿填写他人的私人联系方式。"
                  className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 leading-7 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-y"
                />
              </div>

              <div className="field-aux" aria-hidden="true">
                <label htmlFor="contact-website-alt">公司网址（请勿填写）</label>
                <input
                  id="contact-website-alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              {status === "error" && (
                <p
                  role="alert"
                  className="flex items-start gap-2 text-[12.5px] text-accent-800 bg-accent-50 border border-accent-200 rounded-md px-3 py-2.5 leading-6"
                >
                  <i className="ri-error-warning-line mt-0.5" />
                  {errorMessage}
                </p>
              )}

              <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex items-center justify-center gap-1.5 px-6 h-10 rounded-md bg-accent-500 text-primary-950 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-accent-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      提交反馈
                    </>
                  )}
                </button>
                <p className="text-[11.5px] text-foreground-400 leading-5">
                  提交即表示你同意我们为核实内容而联系你。邮箱仅用于回复，不会公开。
                </p>
              </div>
            </form>
          )}
        </section>

        <aside className="space-y-5">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-900 flex items-center gap-2">
              <i className="ri-lightbulb-line text-accent-600" />
              怎样提高线索的采纳率
            </h2>
            <ul className="mt-3 space-y-2.5 text-[12.5px] text-foreground-600 leading-6">
              {[
                "写清具体时间与地点，避免「最近」「某天」这类模糊表述",
                "说明信息来源，例如亲眼所见、公开文件或当事人转述",
                "如果有截图、合同等材料，可先描述内容，编辑会主动联系",
                "请勿提交他人身份证号、住址、私人电话等敏感信息",
              ].map((tip) => (
                <li key={tip} className="flex items-start gap-2">
                  <i className="ri-check-line text-accent-600 mt-0.5" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg bg-primary-950 text-background-100 p-5">
            <h2 className="font-heading text-base font-bold text-background-50">处理时效</h2>
            <div className="mt-3 space-y-3">
              {[
                { label: "线索受理", value: "48 小时内" },
                { label: "内容纠错", value: "24 小时内" },
                { label: "合作咨询", value: "3 个工作日内" },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between text-[12.5px]">
                  <span className="text-background-300">{item.label}</span>
                  <span className="text-accent-400 font-medium">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-accent-50 p-5">
            <h2 className="font-heading text-base font-bold text-primary-900">可以匿名吗</h2>
            <p className="text-[12.5px] text-foreground-600 leading-6 mt-2">
              可以。姓名栏留空即可，但邮箱仍需填写，方便编辑在核对细节时联系你。我们不会公开任何投稿人信息。
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}